"use client";

import { useState, useCallback, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import TripForm from "@/components/TripForm";
import { useToast } from "@/components/ui/use-toast";
import { MapPin, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";

interface TripFormData {
  destination: string;
  city: string; // Added separate city field for geolocation features
  startDate?: Date;
  endDate?: Date;
  flightDetails?: string;
  preferences?: string;
  mustVisitPlaces?: string;
  budget?: string;
  travelStyle?: string;
}

interface GenerationStatus {
  currentDay: number;
  totalDays: number;
  tripId: string | null;
  completed: boolean;
}

// Retry utility configurations
interface RetryOptions {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffFactor: number;
}

const defaultRetryOptions: RetryOptions = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 5000,
  backoffFactor: 2,
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function withRetry<T>(
  operation: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const config = { ...defaultRetryOptions, ...options };
  let lastError: any;
  let delay = config.initialDelayMs;

  for (let attempt = 1; attempt <= config.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;

      // Check if we should retry based on the error status
      const shouldRetry = error.status >= 500 || error.status === 429;

      if (!shouldRetry || attempt === config.maxRetries) {
        throw error;
      }

      console.warn(
        `API call failed (attempt ${attempt}/${config.maxRetries}):`,
        `Error: ${error.message}.`,
        `Retrying in ${delay}ms...`
      );

      await sleep(delay);
      delay = Math.min(delay * config.backoffFactor, config.maxDelayMs);
    }
  }

  throw lastError;
}

export default function CreateTrip() {
  const { data: session } = useSession();
  const router = useRouter();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<TripFormData | null>(null);
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus>({
    currentDay: 3,
    totalDays: 5,
    tripId: null,
    completed: false,
  });

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const fetchImage = useCallback(async () => {
    if (!formData) return;
    setImageUrl(null);

    // Parse location to extract city and country
    const parseLocation = (location: string | null | undefined) => {
      if (!location) return { city: null, country: null };
      
      // Check if location is in format "{city, country}" or "city, country"
      const match = location.match(/^\{?([^,}]+),\s*([^}]+)\}?$/);
      if (match) {
        return {
          city: match[1].trim(),
          country: match[2].trim(),
        };
      }
      
      // If no comma, treat entire string as city
      return {
        city: location.trim(),
        country: null,
      };
    };

    const location = formData.city || formData.destination || "";
    const { city, country } = parseLocation(location);

    const fetchImageWithQuery = async (searchTerm: string): Promise<boolean> => {
      try {
        const response = await fetch(
          `/api/proxy/images?q=${encodeURIComponent(searchTerm + ' landmark')}`
        );

        if (!response.ok) return false;

        const data = await response.json();
        const newImageUrl = data.results[0]?.url;

        if (newImageUrl) {
          setImageUrl(newImageUrl);
          return true;
        }
        return false;
      } catch (error) {
        return false;
      }
    };

    try {
      setImageLoading(true);

      // Try city first
      if (city) {
        const success = await fetchImageWithQuery(city);
        if (success) {
          setImageLoading(false);
          return;
        }
      }

      // Fallback to country if city failed
      if (country) {
        const success = await fetchImageWithQuery(country);
        if (success) {
          setImageLoading(false);
          return;
        }
      }

      // If both failed, try the original location string as fallback
      if (location && location !== city && location !== country) {
        await fetchImageWithQuery(location);
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
    } finally {
      setImageLoading(false);
    }
  }, [formData]);

  useEffect(() => {
    fetchImage();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return () => {};
  }, [formData]);

  const generateDay = async (tripId: string, dayNumber: number) => {
    const makeRequest = async () => {
      const response = await fetch("/api/generate-day", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tripId, dayNumber }),
      });

      if (!response.ok) {
        const error = new Error(await response.text());
        (error as any).status = response.status;
        throw error;
      }

      return response.json();
    };

    try {
      await withRetry(makeRequest, {
        maxRetries: 3,
        initialDelayMs: 1000,
        maxDelayMs: 5000,
      });

      const isLastDay = dayNumber === generationStatus.totalDays;
      setGenerationStatus((prev) => ({
        ...prev,
        currentDay: dayNumber,
        completed: isLastDay,
      }));

      return true;
    } catch (error) {
      toast({
        title: "Error generating day " + dayNumber,
        description:
          error instanceof Error ? error.message : "Failed to generate day",
        variant: "destructive",
      });
      return false;
    }
  };

  const onSubmit = async (data: TripFormData, useAI: boolean) => {
    if (!session) {
      toast({
        title: "Please log in",
        description: "You need to be logged in to create a trip plan.",
        variant: "destructive",
      });
      router.push("/login");
      return;
    }

    setIsLoading(true);
    setFormData(data);

    const createTrip = async () => {
      const response = await fetch("/api/generate-trip", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          startDate: data.startDate?.toISOString(),
          endDate: data.endDate?.toISOString(),
          useAI,
        }),
      });

      if (!response.ok) {
        const error = new Error(await response.text());
        (error as any).status = response.status;
        throw error;
      }

      return response.json();
    };

    try {
      const result = await withRetry(createTrip);

      if (!useAI) {
        router.push(`/trips/${result.tripId}`);
        return;
      }

      setGenerationStatus({
        currentDay: 0,
        totalDays: result.totalDays,
        tripId: result.tripId,
        completed: false,
      });

      let allDaysGenerated = true;
      let currentDay = 1;

      while (currentDay <= result.totalDays) {
        const success = await generateDay(result.tripId, currentDay);

        if (!success) {
          // If generation fails, retry the same day up to 2 more times
          let retryCount = 0;
          let retrySuccess = false;

          while (retryCount < 2 && !retrySuccess) {
            await sleep(2000); // Wait before retrying
            retrySuccess = await generateDay(result.tripId, currentDay);
            retryCount++;
          }

          if (!retrySuccess) {
            allDaysGenerated = false;
            break;
          }
        }

        if (currentDay === result.totalDays) {
          await sleep(1000); // Small delay for visual feedback
          router.push(`/trips/${result.tripId}`);
        }

        currentDay++;
      }

      if (!allDaysGenerated) {
        toast({
          title: "Generation incomplete",
          description: "Some days could not be generated. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to create trip",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white md:mt-8 mb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <AnimatePresence mode="wait">
          {!generationStatus.tripId ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="text-center sm:text-left space-y-2 mb-6">
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Plan Your Trip
                </h1>
                <p className="text-sm sm:text-base text-gray-600 max-w-2xl">
                  Create a personalized itinerary with AI-powered recommendations or start with a blank template
                </p>
              </div>
              <TripForm onSubmit={onSubmit} isLoading={isLoading} />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="space-y-6"
            >
              <div className="relative w-full rounded-xl overflow-hidden shadow-lg">
                {/* Background container */}
                <div className="absolute inset-0 w-full h-full">
                  {imageUrl ? (
                    <div className="w-full h-full">
                      <Image
                        src={imageUrl}
                        alt={`${formData?.destination} background`}
                        fill
                        className="object-cover brightness-[0.4]"
                        unoptimized={true}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600" />
                  )}
                </div>

                {/* Content overlay */}
                <div className="relative py-8 sm:py-12 px-6 text-center">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    className="inline-flex items-center justify-center space-x-3 mb-3"
                  >
                    <MapPin className="h-7 w-7 text-white drop-shadow-lg" />
                    <h2 className="text-2xl sm:text-3xl font-semibold text-white drop-shadow-lg">
                      {formData?.destination || "Iceland"}
                    </h2>
                  </motion.div>
                  <p className="text-gray-100 text-sm sm:text-base drop-shadow-md">
                    Creating your personalized itinerary
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
                <div className="text-center">
                  <p className="text-base font-medium text-gray-700 mb-1">
                    {generationStatus.currentDay === generationStatus.totalDays
                      ? "Finalizing your trip..."
                      : generationStatus.currentDay === 0
                      ? "Initializing..."
                      : `Planning day ${generationStatus.currentDay} of ${generationStatus.totalDays}`}
                  </p>
                  <div className="w-full max-w-xs mx-auto h-2 bg-slate-200 rounded-full overflow-hidden mt-3">
                    <motion.div
                      className="h-full bg-gradient-to-r from-indigo-600 to-purple-600"
                      initial={{ width: 0 }}
                      animate={{
                        width: `${(generationStatus.currentDay / generationStatus.totalDays) * 100}%`,
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                    />
                  </div>
                </div>

                <div className="space-y-2.5">
                  {Array.from(
                    { length: generationStatus.totalDays },
                    (_, i) => i + 1
                  ).map((day) => (
                    <motion.div
                      key={day}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{
                        opacity: 1,
                        x: 0,
                      }}
                      transition={{ delay: day * 0.05 }}
                      className={`
                        w-full p-4 rounded-lg flex items-center justify-between
                        border transition-all duration-300
                        ${
                          day <= generationStatus.currentDay
                            ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white border-indigo-500 shadow-md"
                            : "bg-white text-gray-500 border-slate-200 hover:border-slate-300"
                        }
                      `}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold">Day {day}</span>
                        {day === generationStatus.currentDay && (
                          <motion.span
                            className="text-xs opacity-90"
                            animate={{ opacity: [0.7, 1, 0.7] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          >
                            In progress...
                          </motion.span>
                        )}
                      </div>
                      <div className="flex items-center">
                        {day < generationStatus.currentDay && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200 }}
                          >
                            <Check className="h-5 w-5" />
                          </motion.div>
                        )}
                        {day === generationStatus.currentDay && (
                          <motion.div
                            className="h-2.5 w-2.5 rounded-full bg-white"
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          />
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
